/* SUMMARY
  * Imports
  * Components
  * Helpers
  * ConnectedGuard: Is a components use to check if the user is connected.
*/

/* Imports */
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
/***/

/* Components */
import Loading from "../../../components/loading/Loading";
import GmapsLoaded from "../../gmap/GmapLoaded";
import LanguageInitializer from "../../../components/language-initializer/LanguageInitializer";
import PreferencesInitializer from "../../../components/preferences-initializer/PreferencesInitializer";
import UnauthorizedAccess from "../../../views/unauthorized-access/UnauthorizedAccess";
/***/

/* Helpers */
/**
 * Vérifie si l'utilisateur a uniquement le rôle ROLE_TECH
 * Les utilisateurs avec ce rôle seul n'ont pas accès à la plateforme web
 */
const isTechnicianOnly = (roles: string[] | undefined): boolean => {
  if (!roles || roles.length === 0) return false;
  
  // Rôles qui donnent accès à la plateforme web
  const webAccessRoles = ["ROLE_SUPER_ADMIN", "ROLE_ADMIN", "ROLE_PLANNER"];
  
  // Vérifie si l'utilisateur a au moins un rôle qui donne accès au web
  const hasWebAccess = roles.some(role => webAccessRoles.includes(role));
  
  // Si l'utilisateur n'a pas de rôle web mais a ROLE_TECH, il est technicien uniquement
  return !hasWebAccess && roles.includes("ROLE_TECH");
};
/***/

/*
 * ConnectedGuard: Is a components use to check if the user is connected.
 * If he is, it will redirect him to the page he ask,
 * if is not, it will be redirected to login
*/
const ConnectedGuard = ({children}: {children: React.ReactNode}) => {
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { initialized, keycloak } = useKeycloak();

  useEffect(() => {
    if (initialized) setLoading(false);
    setIsAuth(keycloak.authenticated || false);
  }, [keycloak.authenticated, initialized]);

  if (!loading) {
    if (!isAuth) keycloak.login();
    else {
      // Vérifier si l'utilisateur est un technicien sans accès web
      const userRoles = keycloak.realmAccess?.roles;
      if (isTechnicianOnly(userRoles)) {
        return <UnauthorizedAccess />;
      }

      return (
        <GmapsLoaded>
          <LanguageInitializer>
            <PreferencesInitializer>
              {location.pathname === "/" || location.pathname === "" ? 
                <Navigate to="/dashboard" replace />
              :
                children
              }
            </PreferencesInitializer>
          </LanguageInitializer>
        </GmapsLoaded>
      );
    }
  } else {
    return <Loading/>;
  }
};
/***/

export default ConnectedGuard;
